import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  getBrandDefaultCountry,
  getBrandCountryOptions,
  getBrandPhonePlaceholder,
  isBrandPhoneValid,
  normalizeBrandPhone,
} from '../../shared/utils/brandCountry';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCheck, Search, Plus, Edit, Trash2, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Users, DollarSign, Building, MapPin, Phone, Mail, FileText, List, LayoutList, Briefcase, Tag } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { useDocuments } from '../../core/contexts/DocumentsContext';
import { fmtFooterDa, totalsForFilteredClients } from './utils/tableListFooterTotals';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { facturationTableHeadClass, facturationTableScrollClass, facturationTableShellClass, QAWAEM_KPI_GRID_CLASS, sliceQawaemStats } from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import QawaemStatsCard from '../../products/saudi/components/QawaemStatsCard';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableToolbarSearch from '../../products/saudi/components/QawaemTableToolbarSearch';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { crmContactToClientForm, buildCrmContactSelectOptions } from '../../shared/utils/crmCrossModuleUtils';
import { dispatchCrmApiRefresh, CRM_CONTACTS_REFRESH_EVENT, FACTURATION_CLIENTS_REFRESH_EVENT } from '../crm/crmRefreshEvents';
import QawaemTableCardsViewToggle from '../../products/saudi/components/QawaemTableCardsViewToggle';
import QawaemImportExportToolbar from '../../products/saudi/components/QawaemImportExportToolbar';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import {
  QawaemTableStickyActionsTd,
} from '../../products/saudi/components/QawaemTableStickyActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
// Tables-mobile.css - DELETED (not needed)

registerLocale('fr', fr);
registerLocale('ar', arLocale);

const buildEmptyClientForm = (clientNumber = '') => ({
  client_number: clientNumber,
  client_type: '',
  status: '',
  company_name: '',
  contact_person: '',
  contact_fonction: '',
  destinataire_name: '',
  destinataire_fonction: '',
  email: '',
  phone1: '',
  phone2: '',
  phone3: '',
  address: '',
  city: '',
  region: '',
  postal_code: '',
  country: getBrandDefaultCountry(),
  fiscal_id: '',
  article: '',
  rc_number: '',
  nis_number: '',
  payment_terms: '',
  notes: '',
});

/**
 * Inline edit cell — MUST stay outside ClientsPage.
 * Same pattern as Facturiers/Fournisseurs: plain clickable text, tooltip only.
 */
function ClientEditableCell({
  recordId,
  field,
  value,
  className = 'text-white font-semibold',
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  readOnly = true,
  displayValue,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSaveRef.current = true;
            onSave(recordId, field, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(recordId, field, value);
        }
      }}
      className={`${className} cursor-pointer`}
      title={t('modules.facturation.pages.clientsPage.click_to_edit')}
    >
      {value || '-'}
    </span>
  );
}

const ClientsPage = () => {
  const { t, i18n } = useTranslation();
  const p = useCallback((key, opts) => t(`modules.facturation.pages.clientsPage.${key}`, opts), [t]);
  const { settings } = useSettings();
  const facturationConfig = useFacturationConfig();
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const phonePlaceholder = getBrandPhonePlaceholder();

  const countryOptions = useMemo(
    () => getBrandCountryOptions((key) => p(key)),
    [p],
  );

  const getTypeLabel = (type) => {
    if (type === 'entreprise') return p(qawaem ? 'type_company' : 'type_entreprise');
    if (type === 'institution') return p(qawaem ? 'type_government' : 'type_institution');
    return p(qawaem ? 'type_individual' : 'type_particulier');
  };

  const getStatusLabel = (status) => (status === 'active' ? p('status_active') : p('status_inactive'));

  const fiscalIdLabel = (short = false) => p(qawaem ? (short ? 'label_trn_short' : 'label_trn') : (short ? 'label_nif_short' : 'label_nif'));
  const rcLabel = () => p(qawaem ? 'label_cr' : 'label_rc');
  const rcNumberLabel = () => p(qawaem ? 'label_cr_number' : 'label_rc_number');

  const getExportHeaders = useCallback(() => {
    const headers = [
      { key: 'client_number', label: p('col_client_number') },
      { key: 'company_name', label: p('col_name') },
      { key: 'contact_person', label: p('col_contact') },
      { key: 'email', label: p('col_email') },
      { key: 'phone1', label: p('col_phone1') },
      { key: 'phone2', label: p('col_phone2') },
      { key: 'phone3', label: p('col_phone3') },
      { key: 'address', label: p('col_address') },
      { key: 'city', label: p('col_city') },
      { key: 'region', label: p('col_region') },
      { key: 'postal_code', label: p('col_postal_code') },
      { key: 'country', label: p('col_country') },
      { key: 'fiscal_id', label: fiscalIdLabel(true) },
      { key: 'article', label: p('label_article_short') },
      { key: 'rc_number', label: rcLabel() },
      { key: 'nis_number', label: p('label_nis') },
      { key: 'client_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
    ];
    if (qawaem) {
      return headers.filter((h) => !['article', 'nis_number'].includes(h.key));
    }
    return headers;
  }, [p, qawaem]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [crmContacts, setCrmContacts] = useState([]);
  const [linkedCrmContactKey, setLinkedCrmContactKey] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    if (!showModal) return undefined;
    let cancelled = false;
    const loadCrmContacts = async () => {
      try {
        const response = await localApiAdapter.get('/crm/contacts');
        if (cancelled) return;
        const rows = Array.isArray(response) ? response : (response?.data || []);
        setCrmContacts(rows);
      } catch (error) {
        console.error('Error loading CRM contacts for client form:', error);
        if (!cancelled) setCrmContacts([]);
      }
    };
    void loadCrmContacts();
    window.addEventListener(CRM_CONTACTS_REFRESH_EVENT, loadCrmContacts);
    return () => {
      cancelled = true;
      window.removeEventListener(CRM_CONTACTS_REFRESH_EVENT, loadCrmContacts);
    };
  }, [showModal]);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);
  
  // Inline editing states
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  const [clientFormTab, setClientFormTab] = useState('general');

  const clientFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_general'), icon: Building, iconColor: 'text-blue-400' },
    { id: 'contact', label: p('section_contact_details'), icon: Phone, iconColor: 'text-green-400' },
    { id: 'fiscal', label: p('section_fiscal'), icon: FileText, iconColor: 'text-purple-400' },
  ]), [p]);
  
  const [formData, setFormData] = useState(() => buildEmptyClientForm());
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_clients');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Inline editing dropdowns
  const [openTypeDropdown, setOpenTypeDropdown] = useState(null);
  const [typeDropdownPosition, setTypeDropdownPosition] = useState(null);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadClients(); }, []);
  useEffect(() => {
    const handler = () => { void loadClients(); };
    window.addEventListener(FACTURATION_CLIENTS_REFRESH_EVENT, handler);
    return () => window.removeEventListener(FACTURATION_CLIENTS_REFRESH_EVENT, handler);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadClients = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/clients');
      const raw = response?.data !== undefined ? response.data : response;
      setClients(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    entreprise: clients.filter(c => c.client_type === 'entreprise').length,
    particulier: clients.filter(c => c.client_type === 'particulier').length,
    institution: clients.filter(c => c.client_type === 'institution').length
  };

  const mainTabs = [
    { id: 'all_clients', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: Building },
    { id: 'par_statut', name: p('tab_by_status'), icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: p('filter_inactive'), icon: XCircle },
    { id: 'entreprise', name: p('filter_entreprise'), icon: Building },
    { id: 'particulier', name: p('filter_particulier'), icon: UserCheck },
    { id: 'institution', name: p('filter_institution'), icon: Briefcase }
  ];

  const clientsDistribution = {
    all_clients: {
      title: p('overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactive', name: p('dist_inactive'), count: clients.filter(c => c.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'entreprise', name: p('dist_entreprise'), count: stats.entreprise, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'particulier', name: p('dist_particulier'), count: stats.particulier, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'institution', name: p('dist_institution'), count: stats.institution, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    par_type: {
      title: p('distribution_by_type'),
      data: [
        { filterKey: 'entreprise', name: p('dist_entreprise'), count: stats.entreprise, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'particulier', name: p('dist_particulier'), count: stats.particulier, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'institution', name: p('dist_institution'), count: stats.institution, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactive', name: p('dist_inactive'), count: clients.filter(c => c.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  };

  const distributionInfo = clientsDistribution[mainViewMode] || clientsDistribution.all_clients;

  const columnFilterRules = useMemo(() => [
    textColumnRule('client_number', (row) => row.client_number),
    textColumnRule('company_name', (row) => row.company_name),
    textColumnRule('email', (row) => row.email),
    textColumnRule('city', (row) => row.city),
    exactColumnRule('client_type', (row) => row.client_type),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredClients = useMemo(() => {
    const base = clients.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const createdDate = new Date(c.created_at);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (createdDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdDate > endOfDay) return false;
      }
    }
    
    if (searchTerm && !c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) && !c.email?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_clients') {
      if (quickFilter === 'active' && c.status !== 'active') return false;
      if (quickFilter === 'inactive' && c.status !== 'inactive') return false;
      if (quickFilter === 'entreprise' && c.client_type !== 'entreprise') return false;
      if (quickFilter === 'particulier' && c.client_type !== 'particulier') return false;
      if (quickFilter === 'institution' && c.client_type !== 'institution') return false;
    }
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_clients':
          if (activeDistributionFilter === 'active' && c.status !== 'active') return false;
          if (activeDistributionFilter === 'inactive' && c.status !== 'inactive') return false;
          if (activeDistributionFilter === 'entreprise' && c.client_type !== 'entreprise') return false;
          if (activeDistributionFilter === 'particulier' && c.client_type !== 'particulier') return false;
          if (activeDistributionFilter === 'institution' && c.client_type !== 'institution') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'entreprise' && c.client_type !== 'entreprise') return false;
          if (activeDistributionFilter === 'particulier' && c.client_type !== 'particulier') return false;
          if (activeDistributionFilter === 'institution' && c.client_type !== 'institution') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'active' && c.status !== 'active') return false;
          if (activeDistributionFilter === 'inactive' && c.status !== 'inactive') return false;
          break;
      }
    }
    return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [clients, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, filters, columnFilterRules]);

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredClients);

  const { documents } = useDocuments();
  const clientsFooterInvoices = useMemo(
    () => totalsForFilteredClients(filteredClients, documents),
    [filteredClients, documents]
  );


  const generateClientNumber = useCallback(() => {
    if (facturationConfig.autoAssignClientNumber === false) return '';
    const prefix = getEntityPrefix(settings, 'client', 'CLI');
    return generateEntityReference(prefix, clients, (c) => c.client_number);
  }, [clients, settings, facturationConfig.autoAssignClientNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const clientNumber = (formData.client_number || '').trim() || generateClientNumber();
      if (!clientNumber) {
        alert(p('alert_client_number_required', { defaultValue: 'رقم العميل مطلوب' }));
        return;
      }
      const submitData = {
        ...formData,
        client_number: clientNumber,
        country: formData.country || getBrandDefaultCountry(),
        phone1: normalizeBrandPhone(formData.phone1),
        phone2: normalizeBrandPhone(formData.phone2),
        phone3: normalizeBrandPhone(formData.phone3),
      };
      for (const field of ['phone1', 'phone2', 'phone3']) {
        if (submitData[field] && !isBrandPhoneValid(submitData[field])) {
          alert(p('alert_invalid_phone', { field: p(`label_${field}`) }));
          return;
        }
      }
      if (facturationConfig.requireTaxNumber && !String(submitData.tax_number || submitData.trn || '').trim()) {
        alert(p('alert_tax_number_required'));
        return;
      }
      if (facturationConfig.requireAddress && !String(submitData.address || '').trim()) {
        alert(p('alert_address_required'));
        return;
      }
      if (editingRecord) {
        await localApiAdapter.put(`/facturation/clients/${editingRecord.id}`, submitData);
      } else {
        await localApiAdapter.post('/facturation/clients', submitData);
      }
      dispatchCrmApiRefresh('/facturation/clients');
    } catch (error) {
      console.error('Error saving client:', error);
      alert(p('alert_save_error'));
      return;
    }
    resetForm();
    loadClients();
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      ...buildEmptyClientForm(),
      ...record,
      country: record.country || getBrandDefaultCountry(),
    });
    setClientFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await localApiAdapter.delete(`/facturation/clients/${id}`);
        dispatchCrmApiRefresh('/facturation/clients');
        loadClients();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/facturation/clients/${id}`)));
        dispatchCrmApiRefresh('/facturation/clients');
        setSelectedRecords([]);
        loadClients();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  // Export handlers
  const handleExportCSV = () => {
    const headers = getExportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? clients.filter(c => selectedRecords.includes(c.id))
      : filteredClients;
    
    exportToCSV(dataToExport, headers, `clients_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const headers = getExportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? clients.filter(c => selectedRecords.includes(c.id))
      : filteredClients;
    
    await exportToExcel(dataToExport, headers, `clients_${new Date().toISOString().split('T')[0]}.xlsx`, p('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0 
        ? clients.filter(c => selectedRecords.includes(c.id))
        : filteredClients;

      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: p('col_client_number'), value: (c) => c.client_number },
          { label: p('col_name'), value: (c) => c.company_name },
          { label: p('col_contact'), value: (c) => c.contact_person },
          { label: p('col_email'), value: (c) => c.email },
          { label: p('col_phone'), value: (c) => c.phone1 },
          { label: p('col_city'), value: (c) => c.city },
          { label: p('col_type'), value: (c) => getTypeLabel(c.client_type) },
          { label: p('col_status'), value: (c) => getStatusLabel(c.status) },
        ],
        listExportTitle: p('export_list_title_upper'),
        clientTitle: p('export_list_title'),
        printDocumentTitle: p('export_document_title', { date: new Date().toISOString().split('T')[0] }),
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('alert_export_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRecords.length > 0 
      ? clients.filter(c => selectedRecords.includes(c.id))
      : filteredClients;
    
    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportXML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? clients.filter(c => selectedRecords.includes(c.id))
      : filteredClients;
    
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<clients>\n';
    dataToExport.forEach(item => {
      xmlContent += '  <client>\n';
      Object.keys(item).forEach(key => {
        const value = item[key] || '';
        xmlContent += `    <${key}>${value}</${key}>\n`;
      });
      xmlContent += '  </client>\n';
    });
    xmlContent += '</clients>';
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_${new Date().toISOString().split('T')[0]}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportHTML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? clients.filter(c => selectedRecords.includes(c.id))
      : filteredClients;
    
    let htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${p('export_html_title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1e40af; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #3b82f6; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
  </style>
</head>
<body>
  <h1>${p('export_html_title')}</h1>
  <p>${p('export_date_label')} ${new Date().toLocaleDateString(dateLocale)}</p>
  <p>${p('export_total_label', { count: dataToExport.length })}</p>
  <table>
    <thead>
      <tr>
        <th>${p('col_client_number')}</th>
        <th>${p('col_name')}</th>
        <th>${p('col_contact')}</th>
        <th>${p('col_email')}</th>
        <th>${p('col_phone')}</th>
        <th>${p('col_type')}</th>
        <th>${p('col_status')}</th>
      </tr>
    </thead>
    <tbody>`;
    
    dataToExport.forEach(c => {
      htmlContent += `
      <tr>
        <td>${c.client_number || ''}</td>
        <td>${c.company_name || ''}</td>
        <td>${c.contact_person || ''}</td>
        <td>${c.email || ''}</td>
        <td>${c.phone1 || ''}</td>
        <td>${getTypeLabel(c.client_type)}</td>
        <td>${getStatusLabel(c.status)}</td>
      </tr>`;
    });
    
    htmlContent += `
    </tbody>
  </table>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportTXT = () => {
    const dataToExport = selectedRecords.length > 0 
      ? clients.filter(c => selectedRecords.includes(c.id))
      : filteredClients;
    
    let txtContent = `${p('export_list_title_upper')}\n${p('export_date_label')} ${new Date().toLocaleDateString(dateLocale)}\n${p('export_total_label', { count: dataToExport.length })}\n\n`;
    txtContent += '='.repeat(100) + '\n\n';
    
    dataToExport.forEach(c => {
      txtContent += `${p('export_txt_line_client_number', { value: c.client_number || '' })}\n`;
      txtContent += `${p('export_txt_line_name', { value: c.company_name || '' })}\n`;
      txtContent += `${p('export_txt_line_contact', { value: c.contact_person || '' })}\n`;
      txtContent += `${p('export_txt_line_email', { value: c.email || '' })}\n`;
      txtContent += `${p('export_txt_line_phone', { value: c.phone1 || '' })}\n`;
      txtContent += `${p('export_txt_line_city', { value: c.city || '' })}\n`;
      txtContent += `${p('export_txt_line_type', { value: getTypeLabel(c.client_type) })}\n`;
      txtContent += `${p('export_txt_line_status', { value: getStatusLabel(c.status) })}\n`;
      txtContent += '-'.repeat(100) + '\n\n';
    });
    
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Import handlers
  const handleImportCSV = () => {
    const headers = getExportHeaders();

    const schema = {
      requiredFields: ['company_name'],
      validators: {
        email: (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(p('alert_import_errors', { validCount: result.validCount, invalidCount: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(p('alert_import_success', { count: result.validCount }));
        }
        
        if (result.data.length > 0) {
          setClients([...clients, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_file_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    const headers = getExportHeaders();

    const schema = {
      requiredFields: ['company_name'],
      validators: {
        email: (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(p('alert_import_errors', { validCount: result.validCount, invalidCount: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(p('alert_import_success', { count: result.validCount }));
        }
        
        if (result.data.length > 0) {
          setClients([...clients, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_file_error'));
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        
        if (!Array.isArray(data)) {
          alert(p('alert_import_json_invalid'));
          return;
        }
        
        const importedClients = data.map((item, idx) => ({
          ...item,
          id: Date.now() + idx
        }));
        
        setClients([...clients, ...importedClients]);
        alert(p('alert_import_success', { count: importedClients.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('JSON import error:', error);
        alert(p('alert_import_json_error'));
      }
    }, '.json');
  };

  const handleImportXML = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        
        const clientsNodes = xmlDoc.getElementsByTagName('client');
        const importedClients = [];
        
        for (let i = 0; i < clientsNodes.length; i++) {
          const node = clientsNodes[i];
          const client = {
            id: Date.now() + i,
            client_number: node.getElementsByTagName('client_number')[0]?.textContent || '',
            company_name: node.getElementsByTagName('company_name')[0]?.textContent || '',
            contact_person: node.getElementsByTagName('contact_person')[0]?.textContent || '',
            email: node.getElementsByTagName('email')[0]?.textContent || '',
            phone1: node.getElementsByTagName('phone1')[0]?.textContent || '',
            phone2: node.getElementsByTagName('phone2')[0]?.textContent || '',
            phone3: node.getElementsByTagName('phone3')[0]?.textContent || '',
            address: node.getElementsByTagName('address')[0]?.textContent || '',
            city: node.getElementsByTagName('city')[0]?.textContent || '',
            fiscal_id: node.getElementsByTagName('fiscal_id')[0]?.textContent || '',
            article: node.getElementsByTagName('article')[0]?.textContent || '',
            rc_number: node.getElementsByTagName('rc_number')[0]?.textContent || '',
            nis_number: node.getElementsByTagName('nis_number')[0]?.textContent || '',
            client_type: node.getElementsByTagName('client_type')[0]?.textContent || 'entreprise',
            status: node.getElementsByTagName('status')[0]?.textContent || 'active'
          };
          importedClients.push(client);
        }
        
        setClients([...clients, ...importedClients]);
        alert(p('alert_import_success', { count: importedClients.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('XML import error:', error);
        alert(p('alert_import_xml_error'));
      }
    }, '.xml');
  };

  const handleImportTXT = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const lines = content.split('\n').filter(line => line.trim());
        
        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        const headers = lines[0].split(delimiter).map(h => h.trim());
        
        const importedClients = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(delimiter);
          const client = { id: Date.now() + i };
          
          headers.forEach((header, index) => {
            client[header.toLowerCase().replace(/\s+/g, '_')] = values[index]?.trim() || '';
          });
          
          importedClients.push(client);
        }
        
        setClients([...clients, ...importedClients]);
        alert(p('alert_import_success', { count: importedClients.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('TXT import error:', error);
        alert(p('alert_import_txt_error'));
      }
    }, '.txt,.tsv');
  };

  const crmContactOptions = useMemo(
    () => buildCrmContactSelectOptions(crmContacts),
    [crmContacts],
  );

  const handleCrmContactImport = (key) => {
    setLinkedCrmContactKey(key || '');
    if (!key || !key.startsWith('crm:')) return;
    const contact = crmContacts.find((c) => String(c.id) === key.slice(4));
    if (!contact) return;
    setFormData((prev) => crmContactToClientForm(contact, prev));
  };

  const openNewClientModal = () => {
    setFormData(buildEmptyClientForm(generateClientNumber()));
    setEditingRecord(null);
    setClientFormTab('general');
    setLinkedCrmContactKey('');
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData(buildEmptyClientForm());
    setEditingRecord(null);
    setClientFormTab('general');
    setLinkedCrmContactKey('');
    setShowModal(false);
  };

  
  const {
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredClients);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOpenTypeDropdown(null);
    setTypeDropdownPosition(null);
  }, [selectedRecords]);

  const formGridClass = qawaem ? 'grid grid-cols-1 md:grid-cols-3 gap-4' : 'grid grid-cols-1 md:grid-cols-3 gap-4';

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];
  
  const typeOptions = [
    { value: 'entreprise', label: p(qawaem ? 'type_company' : 'type_entreprise'), icon: Building, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'particulier', label: p(qawaem ? 'type_individual' : 'type_particulier'), icon: UserCheck, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'institution', label: p(qawaem ? 'type_government' : 'type_institution'), icon: Briefcase, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
  ];
  
  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const record = clients.find((c) => c.id === recordId);
      if (!record) return;
      const updatedData = {
        ...record,
        status: newStatus
      };
      await localApiAdapter.put(`/facturation/clients/${record.id}`, updatedData);
      dispatchCrmApiRefresh('/facturation/clients');
      await loadClients();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  // Inline editing functions
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = clients.find(c => c.id === recordId);
    if (!record) { cancelEditing(); return; }

    const norm = (v) => String(v ?? '').trim();
    const newVal = norm(value);
    const originalVal = norm(record[field]);
    if (newVal === originalVal) {
      cancelEditing();
      return;
    }
    if (field === 'company_name' && !newVal) {
      cancelEditing();
      return;
    }

    try {
      await localApiAdapter.put(`/facturation/clients/${recordId}`, { [field]: newVal });
      dispatchCrmApiRefresh('/facturation/clients');
      await loadClients();
      cancelEditing();
      setViewingRecord((prev) => (prev && prev.id === recordId ? { ...prev, [field]: newVal } : prev));
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
    }
  };

  // Handle type change
  const handleTypeChange = async (record, newType) => {
    try {
      // Merge with existing record data
      const updatedData = {
        ...record,
        client_type: newType
      };
      await localApiAdapter.put(`/facturation/clients/${record.id}`, updatedData);
      dispatchCrmApiRefresh('/facturation/clients');
      setOpenTypeDropdown(null);
      setTypeDropdownPosition(null);
      await loadClients();
    } catch (error) {
      console.error('❌ Error updating type:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-medium items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  const getTypeBadge = (type) => {
    if (type === 'entreprise') return <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-medium items-center gap-1.5 border bg-blue-500/20 text-blue-400 border-blue-500/30"><Building size={14} />{p(qawaem ? 'type_company' : 'type_entreprise')}</span>;
    if (type === 'institution') return <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-medium items-center gap-1.5 border bg-amber-500/20 text-amber-400 border-amber-500/30"><Briefcase size={14} />{p(qawaem ? 'type_government' : 'type_institution')}</span>;
    return <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-medium items-center gap-1.5 border bg-purple-500/20 text-purple-400 border-purple-500/30"><UserCheck size={14} />{p(qawaem ? 'type_individual' : 'type_particulier')}</span>;
  };
  
  // Clickable Type Badge
  const ClickableTypeBadge = ({ type, record }) => {
    const config = typeOptions.find(t => t.value === type) || typeOptions[0];
    const { label, icon: Icon, style } = config;
    const buttonRef = useRef(null);

    const handleToggleDropdown = (e) => {
      e.stopPropagation();
      
      if (openTypeDropdown === record.id) {
        setOpenTypeDropdown(null);
        setTypeDropdownPosition(null);
      } else {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const dropdownWidth = 200;
          const dropdownHeight = 150;
          
          let left = rect.left + (rect.width / 2);
          let top = rect.bottom + 8;
          
          if (left + (dropdownWidth / 2) > window.innerWidth - 20) {
            left = window.innerWidth - dropdownWidth - 20;
          }
          
          if (left - (dropdownWidth / 2) < 20) {
            left = dropdownWidth / 2 + 20;
          }
          
          if (top + dropdownHeight > window.innerHeight - 20) {
            top = rect.top - dropdownHeight - 8;
          }
          
          const newPosition = { top, left };
          setTypeDropdownPosition(newPosition);
          setOpenTypeDropdown(record.id);
        }
      }
    };

    return (
      <div className="relative inline-block">
        <button
          ref={buttonRef}
          onClick={handleToggleDropdown}
          className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style} hover:opacity-80 transition-all cursor-pointer`}
          title={p('click_change_type')}
        >
          <Icon size={14} />{label}<ChevronDown size={12} className={`transition-transform ${openTypeDropdown === record.id ? 'rotate-180' : ''}`} />
        </button>
        {openTypeDropdown === record.id && typeDropdownPosition && ReactDOM.createPortal(
          <div 
            className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden min-w-[200px] transform -translate-x-1/2" 
            style={{ position: 'fixed', zIndex: 99999, top: `${typeDropdownPosition.top}px`, left: `${typeDropdownPosition.left}px`, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {typeOptions.map(option => {
              const OptionIcon = option.icon;
              return (
                <button 
                  key={option.value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTypeChange(record, option.value);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left ${option.value === type ? 'bg-blue-500/25 shadow-inner' : ''}`}
                >
                  <OptionIcon size={16} className={option.style.split(' ')[1].replace('text-', 'text-')} />
                  <span className="text-white text-sm font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
      </div>
    );
  };

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_active') },
    { value: 'inactive', label: p('status_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? p('status_label')
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status_label'));

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'client_number', label: p('col_client_number'), required: true },
    { key: 'company_name', label: p('col_name'), required: true },
    { key: 'contact_person', label: p('col_contact') },
    { key: 'email', label: p('col_email') },
    { key: 'phone1', label: p('col_phone1') },
    { key: 'client_type', label: p('col_type') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemClientColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 2 }),
    [qawaemBaseColumns, showCol],
  );

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          {qawaem ? (
            <>
              <QawaemPageTitleWithFilters
                titleSuffix={<QawaemTableCardsViewToggle value={viewMode} onChange={setViewMode} />}
                filters={
                  <>
                    {selectedRecords.length > 0 ? (
                      <button
                        type="button"
                        onClick={handleBulkDelete}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                      >
                        <Trash2 size={16} />
                        <span>{p('delete_count', { count: selectedRecords.length })}</span>
                      </button>
                    ) : null}
                    <QawaemTableToolbarSearch
                      viewMode={viewMode}
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder={p('search_placeholder')}
                    />
                  </>
                }
              >
                <h1 className="text-3xl font-bold text-forest-900 flex items-center gap-3">
                  <UserCheck className="page-title-icon page-title-icon--bronze h-8 w-8" />
                  {p('title')}
                </h1>
              </QawaemPageTitleWithFilters>
              <p className="page-section-desc">{p('subtitle')}</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <UserCheck className="h-8 w-8 text-blue-400" />
                {p('title')}
              </h1>
              <p className="text-white/60 mt-2">{p('subtitle')}</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!qawaem ? (
            <>
              <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
                <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={openNewClientModal}>
                <Plus className="h-5 w-5" /><span>{p('create_client')}</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn-stat-green btn-header-compact shrink-0" onClick={openNewClientModal}>
                <span className="hidden sm:inline">{p('create_client')}</span>
                <Plus className="h-4 w-4" />
              </button>
              <QawaemImportExportToolbar
                compact
                importOpen={showImportDropdown}
                exportOpen={showExportDropdown}
                onToggleImport={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }}
                onToggleExport={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }}
                importRef={importDropdownRef}
                exportRef={exportDropdownRef}
                importLabel={p('import_label')}
                exportLabel={t('common.export')}
                generatingLabel={p('generating_pdf')}
                isGeneratingPDF={isGeneratingPDF}
                onImportCSV={handleImportCSV}
                onImportExcel={handleImportExcel}
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onExportCSV={handleExportCSV}
              />
            </>
          )}
        </div>
      </div>

      {qawaem ? (
        <div className={QAWAEM_KPI_GRID_CLASS}>
          <QawaemStatsCard label={p('stat_total')} value={stats.total} icon={Users} variant="copper" delay={0} />
          <QawaemStatsCard label={p('stat_active')} value={stats.active} icon={CheckCircle} variant="forest" delay={0.08} />
          <QawaemStatsCard label={p('stat_entreprise')} value={stats.entreprise} icon={Building} variant="copper" delay={0.15} />
          <QawaemStatsCard label={p('stat_particulier')} value={stats.particulier} icon={UserCheck} variant="forest" delay={0.22} />
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {sliceQawaemStats([
          { title: p('stat_total'), value: stats.total, icon: Users, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_entreprise'), value: stats.entreprise, icon: Building, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_particulier'), value: stats.particulier, icon: UserCheck, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_institution'), value: stats.institution, icon: Briefcase, color: 'from-amber-500 to-amber-600' }
        ]).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      )}

      {!qawaem ? (
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_clients' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterKey ?? item.name} onClick={() => { const key = item.filterKey ?? item.name; setActiveDistributionFilter(activeDistributionFilter === key ? null : key); }} title={p('filter_by_title', { name: item.name })} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey ?? item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey ?? item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey ?? item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
      ) : null}

      <div className={facturationTableShellClass(qawaem)}>
        {!qawaem ? (

        <div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="search" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
            />
              
              
              {/* Status Filter */}
              <div className="relative" ref={statusFilterRef}>
                <button 
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">
                    {statusFilter === 'all' ? p('status_label') : statusFilter === 'active' ? p('status_active') : p('status_inactive')}
                  </span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', 
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <Filter size={16} className="text-white/50" />
                        <span className="text-sm font-medium">{p('all_statuses')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('active');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'active' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <CheckCircle size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{p('status_active')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('inactive');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'inactive' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <XCircle size={16} className="text-red-400" />
                        <span className="text-sm font-medium">{p('status_inactive')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Date Range Filter */}
              <div className="relative" ref={dateRangeRef}>
                <button 
                  onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setShowImportDropdown(false); setShowExportDropdown(false); }} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('period')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('start_date')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({...dateRange, start: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                    {date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                  </div>
                                  <button
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('end_date')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({...dateRange, end: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                    {date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                  </div>
                                  <button
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                        {(dateRange.start || dateRange.end) && (
                          <button 
                            onClick={() => setDateRange({ start: null, end: null })}
                            className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-200 px-3 py-2 rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-2"
                          >
                            <X size={14} />{p('reset')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="relative" style={{ zIndex: 100001 }} ref={importDropdownRef}>
                <button onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className={`px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 border ${
                  isDashboardLightMode
                    ? 'bg-orange-100 hover:bg-orange-200 text-orange-950 border-orange-300'
                    : 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border-orange-500/30'
                }`}>
                  <Upload size={16} /><span className="text-sm font-medium">{p('import_label')}</span><ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, overflow: 'visible', backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={handleImportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={handleImportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">TXT/TSV</span></button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" style={{ zIndex: 100001 }} ref={exportDropdownRef}>
                <button onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className={`px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 border ${
                  isDashboardLightMode
                    ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-950 border-emerald-300'
                    : 'bg-green-500/20 hover:bg-green-500/30 text-green-200 border-green-500/30'
                }`}>
                  <Download size={16} /><span className="text-sm font-medium">{p('export_label')}</span><ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, overflow: 'visible', backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                    <div className="p-2">
                      <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? p('generating') : t('common.format_pdf')}</span></button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-pink-400" /><span className="text-sm font-medium">HTML</span></button>
                      <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        ) : null}

{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('client_number') ? <TableColumnHeaderFilter columnKey="client_number" label={p('col_client_number')} value={filters.client_number || ''} onChange={(v) => setFilter('client_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('company_name') ? <TableColumnHeaderFilter columnKey="company_name" label={p('col_name')} value={filters.company_name || ''} onChange={(v) => setFilter('company_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('contact_person') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_contact')}</th> : null}
                  {showCol('email') ? <TableColumnHeaderFilter columnKey="email" label={p('col_email')} value={filters.email || ''} onChange={(v) => setFilter('email', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('phone1') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_phones')}</th> : null}
                  <TableColumnHeaderFilter columnKey="city" label={p('col_city')} value={filters.city || ''} onChange={(v) => setFilter('city', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_fiscal_info')}</th>
                  {showCol('client_type') ? <TableColumnHeaderFilter columnKey="client_type" label={p('col_type')} kind="select" options={[{ value: 'entreprise', label: p('filter_entreprise') }, { value: 'particulier', label: p('filter_particulier') }, { value: 'institution', label: p('filter_institution') }]} value={filters.client_type || ''} onChange={(v) => setFilter('client_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={[{ value: 'active', label: p('status_active') }, { value: 'inactive', label: p('status_inactive') }]} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemClientColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemClientColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredClients.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {showCol('client_number') ? (
                      <td className="px-6 py-4">
                        <ClientEditableCell recordId={record.id} field="client_number" value={record.client_number} className="text-white font-semibold"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_number'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        />
                      </td>
                      ) : null}
                      {showCol('company_name') ? (
                      <td className="px-6 py-4">
                        <ClientEditableCell recordId={record.id} field="company_name" value={record.company_name} className="text-white font-semibold"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'company_name'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        />
                      </td>
                      ) : null}
                      {showCol('contact_person') ? (
                      <td className="px-6 py-4"><div className="flex flex-col">
                        <ClientEditableCell recordId={record.id} field="contact_person" value={record.contact_person} className="text-white/90"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'contact_person'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        />
                      </div></td>
                      ) : null}
                      {showCol('email') ? (
                      <td className="px-6 py-4">
                        <ClientEditableCell recordId={record.id} field="email" value={record.email} className="text-white/70"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'email'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        />
                      </td>
                      ) : null}
                      {showCol('phone1') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 text-xs min-w-[8rem]">
                          <div className="text-white/80 flex items-center gap-1 min-w-0">
                            <Phone size={12} className="text-green-400 shrink-0" />
                            <ClientEditableCell recordId={record.id} field="phone1" value={record.phone1} className="text-white/80 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone1'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                          <div className="text-white/70 flex items-center gap-1 min-w-0">
                            <Phone size={12} className="text-blue-400 shrink-0" />
                            <ClientEditableCell recordId={record.id} field="phone2" value={record.phone2} className="text-white/70 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone2'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                          <div className="text-white/60 flex items-center gap-1 min-w-0">
                            <Phone size={12} className="text-purple-400 shrink-0" />
                            <ClientEditableCell recordId={record.id} field="phone3" value={record.phone3} className="text-white/60 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone3'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <ClientEditableCell recordId={record.id} field="city" value={record.city} className="text-white/80"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'city'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 text-xs min-w-[10rem]">
                          <div className="text-white/80 flex items-center gap-1 flex-wrap min-w-0">
                            <span className="text-white/50 shrink-0">{fiscalIdLabel()}</span>
                            <ClientEditableCell recordId={record.id} field="fiscal_id" value={record.fiscal_id} className="text-white/80 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'fiscal_id'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                          {!qawaem && (
                          <div className="text-white/75 flex items-center gap-1 flex-wrap min-w-0">
                            <span className="text-white/50 shrink-0">{p('label_article')}</span>
                            <ClientEditableCell recordId={record.id} field="article" value={record.article} className="text-white/75 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'article'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                          )}
                          <div className="text-white/70 flex items-center gap-1 flex-wrap min-w-0">
                            <span className="text-white/50 shrink-0">{rcLabel()}</span>
                            <ClientEditableCell recordId={record.id} field="rc_number" value={record.rc_number} className="text-white/70 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'rc_number'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                          {!qawaem && (
                          <div className="text-white/60 flex items-center gap-1 flex-wrap min-w-0">
                            <span className="text-white/50 shrink-0">{p('label_nis')}</span>
                            <ClientEditableCell recordId={record.id} field="nis_number" value={record.nis_number} className="text-white/60 min-w-0 flex-1"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'nis_number'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                          )}
                        </div>
                      </td>
                      {showCol('client_type') ? (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><ClickableTypeBadge type={record.client_type} record={record} /></div></td>
                      ) : null}
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view')}
                          editTitle={p('action_edit')}
                          deleteTitle={p('action_delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><UserCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredClients.length })}</span>
                      <span>{p('footer_invoices')} <span className="text-white font-semibold">{clientsFooterInvoices.nbDocs}</span></span>
                      <span>{p('footer_revenue')} <span className="text-white font-semibold">{fmtFooterDa(clientsFooterInvoices.caTotal)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredClients.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-y-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 flex-wrap min-w-0">
                          <Building size={20} className="text-blue-400 shrink-0" />
                          <ClientEditableCell recordId={record.id} field="company_name" value={record.company_name} className="text-xl font-bold text-white break-words"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'company_name'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                        </h3>
                        <div className="flex gap-2"><ClickableTypeBadge type={record.client_type} record={record} /><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_general')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_contact')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="contact_person" value={record.contact_person} className="text-white/90 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'contact_person'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{p('label_email')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="email" value={record.email} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'email'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{p('label_phone1_short')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="phone1" value={record.phone1} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone1'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{p('label_phone2_short')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="phone2" value={record.phone2} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone2'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{p('label_phone3_short')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="phone3" value={record.phone3} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone3'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_contact_details')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_address')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="address" value={record.address} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'address'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{p('label_city')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="city" value={record.city} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'city'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_fiscal')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{fiscalIdLabel()}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="fiscal_id" value={record.fiscal_id} className="text-white/90 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'fiscal_id'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            {!qawaem && (
                            <div><span className="font-medium">{p('label_article')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="article" value={record.article} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'article'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            )}
                            <div><span className="font-medium">{rcNumberLabel()}:</span>{' '}
                              <ClientEditableCell recordId={record.id} field="rc_number" value={record.rc_number} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'rc_number'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            {!qawaem && (
                            <div><span className="font-medium">{p('label_nis')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="nis_number" value={record.nis_number} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'nis_number'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            )}
                            <div><span className="font-medium">{p('label_payment')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="payment_terms" value={record.payment_terms} className="text-white/80 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'payment_terms'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{p('label_notes')}</span>{' '}
                              <ClientEditableCell recordId={record.id} field="notes" value={record.notes} className="text-white/75 inline break-words"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'notes'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><UserCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}

        <FacturationListPaginationBar
          totalItems={facturationListTotal}
          rawPage={facturationListPage}
          onPageChange={setFacturationListPage}
          pageSize={facturationPageSize}
        />
      </div>

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open
          onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
          title={p('view_title')}
          icon={UserCheck}
          maxWidth="1100px"
          testId="client-view-modal"
        >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Building size={18} className="text-blue-400" />{p('section_general')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_type')}</p><div className="mt-1">{getTypeBadge(viewingRecord.client_type)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_status')}</p><div className="mt-1">{getStatusBadge(viewingRecord.status)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_company_name')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="company_name" value={viewingRecord.company_name} className="text-white font-medium"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'company_name'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                    /></div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_contact_person')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="contact_person" value={viewingRecord.contact_person} className="text-white font-medium"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'contact_person'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                    /></div></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_contact_details')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('col_email')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="email" value={viewingRecord.email} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'email'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_phone1')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="phone1" value={viewingRecord.phone1} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'phone1'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_phone2')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="phone2" value={viewingRecord.phone2} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'phone2'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_phone3')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="phone3" value={viewingRecord.phone3} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'phone3'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('col_address')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="address" value={viewingRecord.address} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'address'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('col_city')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="city" value={viewingRecord.city} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'city'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_fiscal_payment')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{fiscalIdLabel(true)}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="fiscal_id" value={viewingRecord.fiscal_id} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'fiscal_id'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      {!qawaem && (
                      <div><p className="text-white/60 text-sm mb-1">{p('label_article_short')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="article" value={viewingRecord.article} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'article'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      )}
                      <div><p className="text-white/60 text-sm mb-1">{rcNumberLabel()}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="rc_number" value={viewingRecord.rc_number} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'rc_number'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      {!qawaem && (
                      <div><p className="text-white/60 text-sm mb-1">{p('label_nis_number')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="nis_number" value={viewingRecord.nis_number} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'nis_number'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      )}
                      <div><p className="text-white/60 text-sm mb-1">{p('label_payment_terms')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="payment_terms" value={viewingRecord.payment_terms} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'payment_terms'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_notes')}</p><div className="text-white font-medium"><ClientEditableCell recordId={viewingRecord.id} field="notes" value={viewingRecord.notes} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'notes'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div></div>
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit_title') : p('modal_create_title')}
          icon={UserCheck}
          formId="client-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          onSubmit
          testId="client-crud-modal"
          tabs={clientFormTabs}
          activeTab={clientFormTab}
          onTabChange={setClientFormTab}
        >
              <form id="client-form" onSubmit={handleSubmit}>
                {clientFormTab === 'general' && (
                  <div className="space-y-4">
                  <div className={formGridClass}>
                          <div className={qawaem ? 'sm:col-span-2' : 'md:col-span-3'}>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('modules.crm.pages.contactsPage.title', { defaultValue: 'استيراد من CRM' })}</label>
                            <CustomSelect
                              value={linkedCrmContactKey}
                              onChange={handleCrmContactImport}
                              options={crmContactOptions}
                              placeholder={t('modules.crm.pages.contactsPage.search_placeholder', { defaultValue: 'اختر جهة اتصال CRM…' })}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_client_number')} required /></label>
                            <input 
                              type="text" 
                              required 
                              value={formData.client_number} 
                              onChange={(e) => setFormData({ ...formData, client_number: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_client_number')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_client_type')}</label>
                            <CustomSelect
                              value={formData.client_type}
                              onChange={(value) => setFormData({ ...formData, client_type: value })}
                              options={[
                                { value: 'entreprise', label: p(qawaem ? 'type_company' : 'type_entreprise'), icon: <Building size={16} className="text-blue-400" /> },
                                { value: 'particulier', label: p(qawaem ? 'type_individual' : 'type_particulier'), icon: <Users size={16} className="text-purple-400" /> },
                                { value: 'institution', label: p(qawaem ? 'type_government' : 'type_institution'), icon: <Briefcase size={16} className="text-amber-400" /> }
                              ]}
                              placeholder={p('placeholder_search_type')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_status')} required /></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'active', label: p('status_active'), icon: <CheckCircle size={14} className="text-green-400" /> },
                                { value: 'inactive', label: p('status_inactive'), icon: <XCircle size={14} className="text-red-400" /> }
                              ]}
                              placeholder={p('placeholder_search_status')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                        </div>
                        <div className={formGridClass}>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_company_name')} required /></label><input type="text" required value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder={p('placeholder_company')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_contact_person')}</label><input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder={p('placeholder_contact')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_contact_function')}</label><input type="text" value={formData.contact_fonction || ''} onChange={(e) => setFormData({ ...formData, contact_fonction: e.target.value })} placeholder={p('placeholder_contact_function')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                        </div>
                        <div className={formGridClass}>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_recipient')}</label><input type="text" value={formData.destinataire_name || ''} onChange={(e) => setFormData({ ...formData, destinataire_name: e.target.value })} placeholder={p('placeholder_recipient')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_recipient_function')}</label><input type="text" value={formData.destinataire_fonction || ''} onChange={(e) => setFormData({ ...formData, destinataire_fonction: e.target.value })} placeholder={p('placeholder_recipient_function')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                        </div>
                  </div>
                )}

                {clientFormTab === 'contact' && (
                  <div className={formGridClass}>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_email')}</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={p('placeholder_email')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_phone1')}</label><input type="tel" value={formData.phone1} onChange={(e) => setFormData({ ...formData, phone1: e.target.value })} placeholder={phonePlaceholder} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_phone2')}</label><input type="tel" value={formData.phone2} onChange={(e) => setFormData({ ...formData, phone2: e.target.value })} placeholder={phonePlaceholder} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_phone3')}</label><input type="tel" value={formData.phone3} onChange={(e) => setFormData({ ...formData, phone3: e.target.value })} placeholder={phonePlaceholder} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_address')}</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder={p('placeholder_address')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_city')}</label><input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder={p('placeholder_city')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_region')}</label><input type="text" value={formData.region || ''} onChange={(e) => setFormData({ ...formData, region: e.target.value })} placeholder={p('placeholder_region')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_postal_code')}</label><input type="text" value={formData.postal_code || ''} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder={p('placeholder_postal_code')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_country')}</label>
                            <CustomSelect
                              value={formData.country}
                              onChange={(value) => setFormData({ ...formData, country: value })}
                              options={countryOptions}
                              placeholder={p('placeholder_country')}
                              searchable
                              creatable={false}
                              autoSelect={false}
                            />
                          </div>
                        </div>
                )}

                {clientFormTab === 'fiscal' && (
                  <div className={formGridClass}>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{fiscalIdLabel(true)}</label><input type="text" value={formData.fiscal_id} onChange={(e) => setFormData({ ...formData, fiscal_id: e.target.value })} placeholder={p(qawaem ? 'placeholder_trn' : 'placeholder_nif')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          {!qawaem && (
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_article_short')}</label><input type="text" value={formData.article} onChange={(e) => setFormData({ ...formData, article: e.target.value })} placeholder={p('placeholder_article')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          )}
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{rcNumberLabel()}</label><input type="text" value={formData.rc_number} onChange={(e) => setFormData({ ...formData, rc_number: e.target.value })} placeholder={p(qawaem ? 'placeholder_cr' : 'placeholder_rc')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          {!qawaem && (
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_nis_number')}</label><input type="text" value={formData.nis_number} onChange={(e) => setFormData({ ...formData, nis_number: e.target.value })} placeholder={p('placeholder_nis')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          )}
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_payment_conditions')}</label><input type="text" value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} placeholder={p('placeholder_payment_terms')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" placeholder={p('placeholder_notes')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"></textarea></div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}
    </div>
  );
};

export default ClientsPage;


